import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTelegram } from '../hooks/useTelegram';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loginWithTelegram, setUser } from '../store/slices/authSlice';
import { getAccessToken } from '../api/client';
import * as invitesApi from '../api/invites';
import * as usersApi from '../api/users';
export function InviteCodePage() {
    const navigate = useNavigate();
    const { initData, haptic } = useTelegram();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((s) => s.auth);
    const [code, setCode] = useState('');
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    // Skip if already invited
    useEffect(() => {
        if (user?.invitedById) {
            navigate('/onboarding', { replace: true });
        }
    }, [user]);
    const handleCodeChange = (e) => {
        const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
        setCode(val);
        setError('');
    };
    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const val = pasted.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
        setCode(val);
        setError('');
    };
    const isCodeValid = /^MUTE[A-Z0-9]{4,8}$/.test(code);
    const handleSubmit = async () => {
        if (!isCodeValid)
            return;
        setStatus('loading');
        setError('');
        try {
            // Ensure we have a valid token before making API calls
            if (!getAccessToken()) {
                if (!initData)
                    throw new Error('No auth');
                await dispatch(loginWithTelegram(initData)).unwrap();
            }
            await invitesApi.activateInvite(code);
            // Auto-set city to Kash (id: 2) — city selection hidden for now
            const updatedUser = await usersApi.updateProfile({ cityId: 2 });
            dispatch(setUser(updatedUser));
            setStatus('success');
            haptic('success');
            setTimeout(() => navigate('/onboarding', { replace: true }), 1000);
        }
        catch {
            setStatus('error');
            setError('Неверный или просроченный код');
            haptic('error');
        }
    };
    return (_jsxs("div", { className: "min-h-[100dvh] flex flex-col items-center justify-center px-10 relative overflow-hidden", style: { background: '#0A0A0A' }, children: [_jsx("div", { className: "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full opacity-[0.08]", style: { background: 'radial-gradient(circle, #10B981 0%, transparent 70%)' } }), _jsxs("div", { className: "relative z-10 flex flex-col items-center w-full", children: [_jsx("div", { className: "self-start mb-8", children: _jsx("button", { className: "btn-back", onClick: () => navigate(-1), children: _jsx(ArrowLeft, { className: "w-4 h-4" }) }) }), _jsx(KeyRound, { className: "w-10 h-10 text-pink-400 mb-8" }), _jsx("h2", { className: "text-xl font-bold text-white mb-2 text-center", children: "\u0418\u043D\u0432\u0430\u0439\u0442" }), _jsx("p", { className: "text-white/60 text-[13px] mb-10 text-center", children: "\u0418\u043D\u0432\u0430\u0439\u0442 \u043C\u043E\u0436\u043D\u043E \u043F\u043E\u043B\u0443\u0447\u0438\u0442\u044C \u0443 \u0443\u0447\u0430\u0441\u0442\u043D\u0438\u043A\u0430 \u043A\u043B\u0443\u0431\u0430" }), _jsx("div", { className: "w-full max-w-[280px]", children: _jsx("input", { type: "text", value: code, onChange: handleCodeChange, onPaste: handlePaste, placeholder: "MUTEXXXX", className: `w-full bg-white/[.12] border text-center text-white text-2xl font-mono font-bold tracking-[4px] rounded-2xl px-6 py-5 outline-none placeholder-white/40 ${status === 'success'
                                ? 'border-emerald-400/30'
                                : status === 'error'
                                    ? 'border-red-400/30'
                                    : 'border-white/[.14]'}`, style: {
                                boxShadow: status === 'success'
                                    ? '0 0 30px rgba(16,185,129,.08)'
                                    : 'none',
                                caretColor: '#EC4899',
                            }, autoComplete: "off", autoCapitalize: "characters", inputMode: "text" }) }), status === 'success' && (_jsxs("p", { className: "text-emerald-400 text-[13px] mt-4 font-medium flex items-center gap-1.5", children: [_jsx(CheckCircle, { className: "w-4 h-4" }), " \u0414\u043E\u0431\u0440\u043E \u043F\u043E\u0436\u0430\u043B\u043E\u0432\u0430\u0442\u044C"] })), error && (_jsx("p", { className: "text-red-400 text-[13px] mt-4 font-medium", children: error })), _jsxs(Button, { className: "mt-8 w-full max-w-[280px]", onClick: handleSubmit, disabled: !isCodeValid || status === 'loading' || status === 'success', children: ["\u041F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C ", _jsx(ArrowRight, { className: "w-4.5 h-4.5" })] })] })] }));
}
